import { ChevronRight, Loader2, Music, Trash2, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export type CreateProjectModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  name: string;
  onNameChange: (v: string) => void;
  selectedFiles: File[];
  fileDurations: Record<string, string>;
  dragActive: boolean;
  isPending: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onSubmit: (e: React.FormEvent) => void;
  onDrag: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onPickFiles: () => void;
  onFilesSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (idx: number) => void;
};

export function CreateProjectModal({
  open,
  onOpenChange,
  name,
  onNameChange,
  selectedFiles,
  fileDurations,
  dragActive,
  isPending,
  fileInputRef,
  onSubmit,
  onDrag,
  onDrop,
  onPickFiles,
  onFilesSelected,
  onRemoveFile,
}: CreateProjectModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[622px] max-w-[95vw] min-h-[490px] gap-6 rounded-[18px] border border-[#E2E8F0] bg-background p-6 shadow-[0_2px_10px_rgba(0,0,0,0.10)]">
        <DialogHeader className="space-y-2 text-left">
          <DialogTitle className="text-[24px] font-semibold leading-[100%] text-[#0F172B]">
            Nouvelle Archive
          </DialogTitle>
          <DialogDescription className="text-sm text-[#45556C]">
            Nommez votre archive et importez les fichiers audio à transcrire.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="project-name-modal" className="text-base font-semibold text-[#0F172B]">
              Nom de l'archive
            </Label>
            <Input
              id="project-name-modal"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Ex: Collection Seconde Guerre Mondiale"
              className="h-11 rounded-[10px] border border-[#E2E8F0] bg-[#F4F4F4] text-sm text-[#0F172B] placeholder-[#94A3B8] focus-visible:bg-white"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label className="text-base font-semibold text-[#0F172B]">
              Fichiers audio des entretiens
            </Label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,.wav,.m4a,audio/*"
              multiple
              className="hidden"
              onChange={onFilesSelected}
            />
            <div
              onDragEnter={onDrag}
              onDragOver={onDrag}
              onDragLeave={onDrag}
              onDrop={onDrop}
              onClick={onPickFiles}
              className={cn(
                "flex min-h-[230px] w-full flex-col items-center justify-center gap-3 rounded-[14px] border border-dashed text-center cursor-pointer transition-colors p-6",
                dragActive
                  ? "border-[#007AFF] bg-[#EFF6FF]"
                  : "border-primary/40 bg-[#EFF5FF]/60 hover:bg-[#EFF5FF]"
              )}
            >
              <Upload className="size-10 text-primary" />
              <div className="space-y-1">
                <p className="text-base font-semibold text-[#45556C]">Glissez-déposez un fichier audio ici ou cliquez pour l'importer</p>
                <p className="text-sm text-muted-foreground/80">MP3, WAV, M4A jusqu'à 100MB</p>
              </div>
            </div>

            {selectedFiles.length > 0 && (
              <div className="flex flex-col gap-3 mt-3 max-h-[160px] overflow-y-auto pr-1">
                {selectedFiles.map((file, idx) => {
                  const duration = fileDurations[file.name] || "--:--";
                  const sizeMo = (file.size / (1024 * 1024)).toFixed(1).replace(".", ",") + " Mo";
                  return (
                    <div
                      key={`${file.name}-${idx}`}
                      className="flex items-center justify-between border border-[#E2E8F0] bg-[#F8FAFC] rounded-[12px] p-3 w-full gap-3"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-[8px] bg-[#EFF1F5] flex items-center justify-center text-[#45556C] shrink-0">
                          <Music className="h-5 w-5" />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                          <span className="text-sm font-semibold text-[#0F172B] truncate">{file.name}</span>
                          <span className="text-xs text-[#94A3B8]">{duration} · {sizeMo}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveFile(idx);
                        }}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] border border-[#E2E8F0] bg-white text-[#FF1700] hover:bg-red-50 hover:text-red-600 transition-colors"
                        aria-label="Supprimer le fichier"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex justify-end mt-2">
            <Button
              type="submit"
              disabled={isPending || !name.trim() || selectedFiles.length === 0}
              className="h-[46px] rounded-[14px] bg-[#007AFF] px-6 text-base font-semibold text-white hover:bg-[#0066D6] flex items-center gap-1.5"
            >
              {isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Transcrire votre fichier
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
